class ScRecommendedProductsSlider extends HTMLElement {
  connectedCallback() {
    if (!this._boundOnFlickityEvent) {
      this._boundOnFlickityEvent = this._onFlickityEvent.bind(this);
      this._boundOnResize = this._onResize.bind(this);
      this._boundOnAssetLoaded = this._onAssetLoaded.bind(this);
    }

    const url = this._normalizeApiUrl(this.getAttribute('data-url'));
    if (url && !this._hydrating) {
      this._hydrateFromApi(url);
    }

    this.addEventListener('flickity:ready', this._boundOnFlickityEvent);
    this.addEventListener('flickity:slide-changed', this._boundOnFlickityEvent);
    this.addEventListener('flickity:slide-settled', this._boundOnFlickityEvent);
    window.addEventListener('resize', this._boundOnResize);

    try {
      this._viewportMode = this._getViewportMode();
      this._scheduleLayoutSync();
      this._scheduleLayoutSync(160);
      this._scheduleNavSync();
      this._scheduleNavSync(160);
    } catch (error) {
    }
  }

  disconnectedCallback() {
    this.removeEventListener('flickity:ready', this._boundOnFlickityEvent);
    this.removeEventListener('flickity:slide-changed', this._boundOnFlickityEvent);
    this.removeEventListener('flickity:slide-settled', this._boundOnFlickityEvent);
    window.removeEventListener('resize', this._boundOnResize);

    if (this._layoutSyncTimer) {
      clearTimeout(this._layoutSyncTimer);
      this._layoutSyncTimer = null;
    }

    if (this._navSyncTimer) {
      clearTimeout(this._navSyncTimer);
      this._navSyncTimer = null;
    }
  }

  async _hydrateFromApi(url) {
    this._hydrating = true;

    try {
      const res = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!res.ok) {
        this._setEmptyState();
        return;
      }

      const payload = await res.json();
      const payloadProducts = payload && payload.products;
      const products = Array.isArray(payloadProducts) ? payloadProducts : [];
      const rendered = this._renderApiProducts(products);

      if (!rendered) {
        this._setEmptyState();
        return;
      }

      this.dataset.mode = 'api-ready';
      this.removeAttribute('data-url');
      this.removeAttribute('data-empty');
      this.hidden = false;
      this.style.display = '';

      await this._reloadCarousel();
      this._scheduleLayoutSync();
      this._scheduleLayoutSync(220);
    } catch (error) {
      this._setEmptyState();
    } finally {
      this._hydrating = false;
    }
  }

  _setEmptyState() {
    this.dataset.mode = 'empty';
    this.dataset.empty = '1';
    this.removeAttribute('data-url');
    this.hidden = true;
    this.style.display = 'none';
  }

  _renderApiProducts(products) {
    const track = this._getTrack();
    const template = this._getCardTemplate();

    if (!track || !template || !products.length) {
      return 0;
    }

    const baseCell = template.content.firstElementChild;
    if (!baseCell) {
      return 0;
    }

    const limit = Number(this.dataset.apiLimit) || products.length;
    const renderedIds = new Set();
    const fragment = document.createDocumentFragment();
    let renderedCount = 0;

    for (const product of products) {
      if (!product || renderedCount >= limit) {
        break;
      }

      const productId = String(product.id || '');
      if (!productId || renderedIds.has(productId)) {
        continue;
      }

      const variant = this._getAvailableVariant(product);
      if (!variant) {
        continue;
      }

      renderedIds.add(productId);

      const cell = baseCell.cloneNode(true);
      this._populateCardCell(cell, product, variant);
      fragment.appendChild(cell);
      renderedCount += 1;
    }

    track.replaceChildren(fragment);
    this._watchImages(track);

    return renderedCount;
  }

  _populateCardCell(cell, product, variant) {
    const productUrl = this._getProductUrl(product);
    const productTitle = product.title || '';

    const link = cell.querySelector('[data-rec-link]');
    if (link) {
      link.href = productUrl;
      link.setAttribute('aria-label', productTitle);
    }

    const title = cell.querySelector('[data-rec-title]');
    if (title) {
      title.textContent = productTitle;
      title.href = productUrl;
    }

    const image = cell.querySelector('[data-rec-image]');
    const imagePlaceholder = cell.querySelector('[data-rec-image-ph]');
    const imageUrl = product.featured_image || (Array.isArray(product.images) ? product.images[0] : '');

    if (image && imageUrl) {
      image.src = imageUrl;
      image.alt = productTitle;
      image.hidden = false;
      if (imagePlaceholder) {
        imagePlaceholder.hidden = true;
      }
    } else if (image) {
      image.hidden = true;
      if (imagePlaceholder) {
        imagePlaceholder.hidden = false;
      }
    }

    const priceNow = cell.querySelector('[data-rec-price-now]');
    if (priceNow) {
      priceNow.textContent = this._formatMoney(product.price);
    }

    const unitPrice = cell.querySelector('[data-rec-unit-price]');
    if (unitPrice) {
      const unitPriceText = this._formatUnitPrice(product, variant);
      unitPrice.textContent = unitPriceText;
      unitPrice.hidden = unitPriceText === '';
    }

    const priceWas = cell.querySelector('[data-rec-price-was]');
    const comparePrice = Number(product.compare_at_price || 0);
    const currentPrice = Number(product.price || 0);

    if (priceWas) {
      if (comparePrice > currentPrice) {
        priceWas.textContent = this._formatMoney(comparePrice);
        priceWas.hidden = false;
      } else {
        priceWas.hidden = true;
      }
    }

    const variantInput = cell.querySelector('[data-rec-variant-id]');
    if (variantInput) {
      if (variant) {
        variantInput.value = variant.id;
        variantInput.disabled = false;
      } else {
        variantInput.value = '';
        variantInput.disabled = true;
      }
    }

    const button = cell.querySelector('[data-rec-add-btn]');
    if (button) {
      const isAvailable = Boolean(variant && variant.available);
      button.disabled = !isAvailable;
      button.classList.toggle('button--primary', isAvailable);
      button.classList.toggle('button--ternary', !isAvailable);
    }
  }

  _getAvailableVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) {
      return null;
    }

    return variants.find((variant) => variant && variant.available) || null;
  }

  _getProductUrl(product) {
    if (product.url) {
      return product.url;
    }

    if (product.handle) {
      const themeVars = window.themeVariables || {};
      const routes = themeVars.routes || {};
      const rootUrl = routes.rootUrl || '/';
      return `${rootUrl}products/${product.handle}`;
    }

    return '#';
  }

  _formatUnitPrice(product, fallbackVariant) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const price = Number(product.price || 0);
    const pricedVariant = variants.find((item) => {
      return item && item.unit_price_measurement && Number(item.price) === price;
    });
    const variant = pricedVariant || fallbackVariant;
    const measurement = variant && variant.unit_price_measurement;

    if (!variant || !measurement || variant.unit_price == null) {
      return '';
    }

    const referenceValue = Number(measurement.reference_value);
    const unit = measurement.reference_unit || '';
    const reference = referenceValue && referenceValue !== 1 ? `${referenceValue} ${unit}` : unit;

    return `${this._formatMoney(variant.unit_price)} / ${reference}`;
  }

  _formatMoney(cents) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(Number(cents || 0));
    }

    const amount = Number(cents || 0) / 100;
    const themeVars = window.themeVariables || {};
    const settings = themeVars.settings || {};
    const currency = settings.cartCurrency || 'USD';

    return amount.toLocaleString(undefined, {
      style: 'currency',
      currency
    });
  }

  _onFlickityEvent() {
    this._scheduleLayoutSync(40);
    this._scheduleNavSync(20);
  }

  _onResize() {
    const nextViewportMode = this._getViewportMode();

    if (nextViewportMode !== this._viewportMode) {
      this._viewportMode = nextViewportMode;
      this._reloadCarousel();
      return;
    }

    this._scheduleLayoutSync(120);
    this._scheduleNavSync(80);
  }

  _onAssetLoaded() {
    this._scheduleLayoutSync(50);
  }

  _getViewportMode() {
    return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
  }

  _getTrack() {
    return this.querySelector('flickity-carousel');
  }

  _normalizeApiUrl(url) {
    if (!url) {
      return '';
    }

    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return url;
    }
  }

  _getCardTemplate() {
    return this.querySelector('[data-rec-card-template]');
  }

  _scheduleLayoutSync(delay = 0) {
    if (this._layoutSyncTimer) {
      clearTimeout(this._layoutSyncTimer);
    }

    this._layoutSyncTimer = setTimeout(() => {
      this._layoutSyncTimer = null;
      this._syncLayout();
    }, delay);
  }

  _scheduleNavSync(delay = 0) {
    if (this._navSyncTimer) {
      clearTimeout(this._navSyncTimer);
    }

    this._navSyncTimer = setTimeout(() => {
      this._navSyncTimer = null;
      this._syncNavButtons();
    }, delay);
  }

  async _reloadCarousel() {
    const track = this._getTrack();
    if (!track) return;

    try {
      if (typeof track.reload === 'function') {
        await track.reload();
      } else if (track.flickityInstance) {
        const flickityInstance = await track.flickityInstance;
        flickityInstance.resize();
      }
    } catch (error) {
    }

    this._scheduleLayoutSync(60);
    this._scheduleLayoutSync(220);
    this._scheduleNavSync(80);
    this._scheduleNavSync(220);
  }

  _watchImages(track) {
    track.querySelectorAll('img').forEach((img) => {
      if (img.dataset.scRecObserved === '1') return;

      img.dataset.scRecObserved = '1';

      if (!img.complete) {
        img.addEventListener('load', this._boundOnAssetLoaded, { once: true });
        img.addEventListener('error', this._boundOnAssetLoaded, { once: true });
      }
    });
  }

  async _syncLayout() {
    const track = this._getTrack();
    if (!track) return;

    const cells = Array.from(track.querySelectorAll('.sc-rec-slider__cell'));
    if (!cells.length) return;

    this._watchImages(track);

    cells.forEach((cell) => {
      cell.style.height = '';

      const card = cell.querySelector('.sc-rec-card');
      if (card) {
        card.style.height = '';
        card.style.minHeight = '';
      }
    });

    let maxHeight = 0;

    cells.forEach((cell) => {
      const card = cell.querySelector('.sc-rec-card');
      const target = card || cell;
      const rectHeight = Math.ceil(target.getBoundingClientRect().height);
      const scrollHeight = Math.ceil(target.scrollHeight || 0);
      const height = Math.max(rectHeight, scrollHeight);

      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    if (!maxHeight) return;

    cells.forEach((cell) => {
      cell.style.height = `${maxHeight}px`;

      const card = cell.querySelector('.sc-rec-card');
      if (card) {
        card.style.height = `${maxHeight}px`;
        card.style.minHeight = `${maxHeight}px`;
      }
    });

    try {
      if (!track.flickityInstance) return;

      const flickityInstance = await track.flickityInstance;
      flickityInstance.resize();

      if (flickityInstance.viewport) {
        flickityInstance.viewport.style.height = `${maxHeight}px`;
      }
    } catch (error) {
    }
  }

  async _syncNavButtons() {
    const prevButton = this.querySelector('[data-action="prev"]');
    const nextButton = this.querySelector('[data-action="next"]');

    if (!prevButton && !nextButton) return;

    const setDisabled = (button, disabled) => {
      if (!button) return;
      button.classList.toggle('disable', disabled);
    };

    const track = this._getTrack();
    if (!track) {
      setDisabled(prevButton, true);
      setDisabled(nextButton, true);
      return;
    }

    try {
      if (!track.flickityInstance) {
        const cells = track.querySelectorAll('.sc-rec-slider__cell');
        const canScroll = cells.length > 1 && (track.scrollWidth - track.clientWidth > 1);

        setDisabled(prevButton, true);
        setDisabled(nextButton, !canScroll);
        return;
      }

      const flickityInstance = await track.flickityInstance;
      if (!flickityInstance) {
        setDisabled(prevButton, true);
        setDisabled(nextButton, true);
        return;
      }

      const slides = Array.isArray(flickityInstance.slides) ? flickityInstance.slides : [];
      const selectedIndex = Number(flickityInstance.selectedIndex || 0);
      const slideCount = slides.length;
      const lastIndex = Math.max(slideCount - 1, 0);

      let canScroll = slideCount > 1;
      const slideableWidth = Number(flickityInstance.slideableWidth || 0);
      const innerWidth = Number((flickityInstance.size && flickityInstance.size.innerWidth) || 0);

      if (slideableWidth > 0 && innerWidth > 0) {
        canScroll = canScroll && (slideableWidth - innerWidth > 1);
      }

      setDisabled(prevButton, !canScroll || selectedIndex <= 0);
      setDisabled(nextButton, !canScroll || selectedIndex >= lastIndex);
    } catch (error) {
      setDisabled(prevButton, true);
      setDisabled(nextButton, true);
    }
  }
}

if (!customElements.get('sc-recommended-products-slider')) {
  customElements.define('sc-recommended-products-slider', ScRecommendedProductsSlider);
}
